import { Box, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { type UseFormReturn } from 'react-hook-form'
import { type ISlot } from '../../../../models/DistributionPlan'
import { notification } from '../../../../components/Notifications'
import GenericButton from '../../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'
import theme from '../../../../theme'
import { defaultAddedQuestionsValues } from './constants'
import CustomTooltip from '../../../../components/CustomTooltip'
import { DivisionCategory } from '../../../../models/Division'
import { AutoImportManager } from '../../../../APIManager'

interface DefaultSurveySelectionProps {
  methods: UseFormReturn<any, any, undefined>
  distributionSeriesId: string | undefined
  shouldLoadLatestDefaults?: boolean
  setStep: React.Dispatch<React.SetStateAction<'SELECTDEFAULT' | 'TOGGLE' | 'FORM'>>
}

export const DefaultSurveySelection: React.FC<DefaultSurveySelectionProps> = ({
  methods,
  distributionSeriesId,
  shouldLoadLatestDefaults = true,
  setStep
}) => {
  const { data: latestSlotData, isLoading, isFetching } = useQuery<ISlot | null>({
    queryKey: ['latestSlot', distributionSeriesId],
    retry: false,
    meta: { silent: true },
    queryFn: async (): Promise<ISlot | null> => {
      const res = await AutoImportManager.getLatestSlotByDistributionId(distributionSeriesId ?? '')
      return res
    },
    enabled: shouldLoadLatestDefaults && !!distributionSeriesId && distributionSeriesId.trim().length > 0
  })

  const useLatestProjectClicked = async (): Promise<void> => {
    try {
      if (!latestSlotData) return
      const filteredAcademicLevelAddedQuestions = {
        ...latestSlotData?.academicLevelAddedQuestions,
        academicUnitCategories: latestSlotData?.academicLevelAddedQuestions?.academicUnitCategories?.filter(
          (q) => q.categoryName !== DivisionCategory.University
        )
      }
      if (filteredAcademicLevelAddedQuestions.academicUnitCategories == null) {
        filteredAcademicLevelAddedQuestions.academicUnitCategories = []
      }
      const prevProjectFields = [
        { key: 'isSlotAnonymous', value: latestSlotData?.isSlotAnonymous },
        { key: 'isRollingResultsEnabled', value: latestSlotData?.isRollingResultsEnabled },
        { key: 'templateId', value: latestSlotData?.surveyTemplate?.templateId },
        { key: 'surveyTemplate', value: latestSlotData?.surveyTemplate },
        { key: 'isAcademicLevelAddedQuestionsAllowed', value: latestSlotData?.isAcademicLevelAddedQuestionsAllowed },
        { key: 'academicLevelAddedQuestions', value: filteredAcademicLevelAddedQuestions ?? defaultAddedQuestionsValues.academicLevelAddedQuestions },
        { key: 'isInstructorAddedQuestionsAllowed', value: latestSlotData?.isInstructorAddedQuestionsAllowed },
        // Top-level on the Slot dump (Backend: slot_schema.py:108), not nested inside
        // instructorAddedQuestions below — copied separately so onSubmit's create-flow
        // carry-over gate (index.tsx) can actually see it.
        { key: 'isDefaultInstructorAddedQuestionsEnabled', value: latestSlotData?.isDefaultInstructorAddedQuestionsEnabled },
        { key: 'instructorAddedQuestions', value: latestSlotData?.instructorAddedQuestions ?? defaultAddedQuestionsValues.instructorAddedQuestions },
        // CR-295 has nothing to carry, deliberately. Unlike the instructor pair above, this type
        // has no slot-level flag and no create-time settings: `create_slot_entry` handles only the
        // instructor and academic-level types and silently ignores `section_added_questions`, and
        // the only thing that enables it is
        // `POST /slots/<slot_id>/section_added_questions/apply_default/` against a project that
        // already exists. Copying a question count into the form would show the admin a value
        // that no request can persist. Re-enabling on the new project is one action once it has
        // been created: pick the same default template.
        { key: 'isAcademicUnitOptOutEnabled', value: latestSlotData?.isAcademicUnitOptOutEnabled },
        { key: 'academicUnitOptOutCategoryIds', value: latestSlotData?.academicUnitOptOutCategoryIds ?? [] },
        { key: 'academicUnitOptOutDeadline', value: latestSlotData?.academicUnitOptOutDeadline },
        { key: 'isInstructorOptOutEnabled', value: latestSlotData?.isInstructorOptOutEnabled ?? false },
        { key: 'instructorOptOutDeadline', value: latestSlotData?.instructorOptOutDeadline },
        { key: 'isInstructorOptInOverrideEnabled', value: latestSlotData?.isInstructorOptInOverrideEnabled ?? true }
      ]
      prevProjectFields.forEach(({ key, value }) => {
        methods.setValue(key, value)
      })

      notification('Latest project settings have been applied successfully', { variant: 'success' })
      setStep('TOGGLE')
    } catch (error) {
      console.error('Error fetching latest project settings', error)
      notification('Error fetching latest project settings', { variant: 'error' })
    }
  }

  const startFromScratchClicked = (): void => {
    setStep('TOGGLE')
  }

  const setToolTipMessage = (isLoading: boolean, isFetching: boolean, latestSlotData: ISlot | null | undefined): string => {
    if (!shouldLoadLatestDefaults) {
      return 'No previous project settings available'
    } else if (isLoading || isFetching) {
      return 'Loading previous project settings...'
    } else if (!isLoading && !isFetching && latestSlotData) {
      return 'Apply previous project settings from: ' + (latestSlotData.term.termName)
    } else {
      return 'No previous project settings available'
    }
  }

  const tooltipMessage =
    <Box>
      <Typography fontSize={theme.spacing(3)}>
        {setToolTipMessage(isLoading, isFetching, latestSlotData)}
      </Typography>
    </Box>

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: theme.spacing(2),
        my: theme.spacing(4)
      }}
    >
      <CustomTooltip
        title={tooltipMessage}
        placement='top'
      >
        <span>
          <GenericButton
            label='Apply Previous Project Settings'
            variant='contained'
            buttonType={ButtonType.Primary}
            buttonSize={ButtonSize.Large}
            onClick={() => { void useLatestProjectClicked() }}
            loading={isLoading || isFetching}
            style={{ width: theme.spacing(70) }}
            disabled={isLoading || isFetching || latestSlotData === undefined}
          />
        </span>
      </CustomTooltip>
      <Box>Or</Box>
      <GenericButton
        label='Start From Scratch'
        variant='contained'
        buttonType={ButtonType.Secondary}
        buttonSize={ButtonSize.Large}
        onClick={startFromScratchClicked}
        style={{ width: theme.spacing(70) }}
      />
    </Box>
  )
}
